import { oktaAuth } from "../../auth/services/oktaAuth";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { VoyagePNLItem } from "../models/VoyagePnLList.model";
import { PnlContext } from "../utils/sub-helpers/Context";
import axios from "axios";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";

export const apiUrl = process.env.REACT_APP_API_URL ?? "default-api-url";
export const api = axios.create();

let cachedToken: string;

api.interceptors.request.use((config) => {
  const token = oktaAuth.getAccessToken() || cachedToken;
  if (token) {
    cachedToken = token;
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    log("REPO - request - missing token");
  }
  return config;
});

const handleApiError = (error: unknown, operation: string) => {
  logE(
    new Error(
      `REPO - ${operation} - failed - status ${axios.isAxiosError(error) ? error.response?.status ?? "unavailable" : "unavailable"}`,
    ),
  );
};

export const fetchVoyagePnlData = async (tc_code?: string, voyage_no?: string) => {
  log("REPO - fetchVoyagePnlData", { tcCode: tc_code, voyageNo: voyage_no });
  try {
    const { data } = await api.get(`${apiUrl}/voyagepnlDriverDetails`, {
      params: { tc_code, voyage_no },
    });
    log("REPO - fetchVoyagePnlData - response", { hasData: data != null });
    return data;
  } catch (error) {
    handleApiError(error, "fetchVoyagePnlData");
    return null;
  }
};

export const hasVoyagePnlSummary = async (tc_code?: string, voyage_no?: string): Promise<boolean> => {
  if (!tc_code || !voyage_no) return false;

  try {
    await api.get(`${apiUrl}/voyagepnlDriverDetails`, {
      params: { tc_code, voyage_no },
    });
    return true;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return false;
    }

    handleApiError(error, "hasVoyagePnlSummary");
    return false;
  }
};

export const postVoyagePnlData = async (payload: Record<string, any>) => {
  log("REPO - postVoyagePnlData");
  try {
    const { data } = await api.post(`${apiUrl}/voyagepnlDrivers`, payload);
    log("REPO - postVoyagePnlData - response", { hasData: data != null });
    return data;
  } catch (error) {
    handleApiError(error, "postVoyagePnlData");
    return null;
  }
};

export const useVoyagePnlData = (tcCode?: string, voyageNo?: string) => {
  const [voyagePnlData, setVoyagePnlData] = useAtom(PnlContext);
  const [hasError, setHasError] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const latestRequestId = useRef(0);

  useEffect(() => {
    log("STATE - useVoyagePnlData - reset", { tcCode, voyageNo });
    // Clear stale voyage data immediately on route-param change.
    setVoyagePnlData(null);
    setHasError(false);

    if (!tcCode || !voyageNo) {
      setIsLoading(false);
      return;
    }

    const requestId = ++latestRequestId.current;
    let isActive = true;

    const fetchVoyPnlData = async () => {
      setIsLoading(true);
      try {
        const pnlData = await fetchVoyagePnlData(tcCode, voyageNo);

        // Ignore out-of-order responses when user navigates quickly between voyages.
        if (!isActive || requestId !== latestRequestId.current) {
          logV("STATE - useVoyagePnlData - stale response");
          return;
        }

        log("STATE - useVoyagePnlData - apply", { hasData: pnlData !== null });
        setVoyagePnlData(pnlData);
        if (pnlData === null) {
          setHasError(true);
        }
      } catch (err) {
        if (!isActive || requestId !== latestRequestId.current) return;
        setHasError(true);
        handleApiError(err, "useVoyagePnlData");
      } finally {
        if (!isActive || requestId !== latestRequestId.current) return;
        setIsLoading(false);
      }
    };

    fetchVoyPnlData();

    return () => {
      isActive = false;
    };
  }, [tcCode, voyageNo, setVoyagePnlData]);

  return { voyagePnlData, setVoyagePnlData, hasError, setHasError, isLoading, setIsLoading };
};

export const postVoyagePnlReviewComments = async (payload: Record<string, any>) => {
  log("REPO - postVoyagePnlReviewComments");
  try {
    const { data } = await api.post(`${apiUrl}/voyagepnlapproval`, payload);
    log("REPO - postVoyagePnlReviewComments - response", { hasData: data != null });
    return data;
  } catch (error) {
    handleApiError(error, "postVoyagePnlReviewComments");
    return null;
  }
};
export const getVoyagePnlList = async (): Promise<VoyagePNLItem[]> => {
  try {
    const { data } = await api.get<VoyagePNLItem[]>(`${apiUrl}/voyagepnlList`);
    log("REPO - getVoyagePnlList - response", { count: data?.length ?? 0 });
    return data ?? [];
  } catch (error) {
    handleApiError(error, "getVoyagePnlList");
    return [];
  }
};
